// Push Notification Utility Functions

// This read `VITE_API_URL`, a variable that's never actually defined anywhere
// (only VITE_BACKEND_URL is) — so it always silently fell through to the
// localhost fallback, in every environment including production. Every
// browser's own localhost:5000 is unreachable, so getVapidPublicKey() always
// failed and push subscription always silently failed right after — meaning
// no user has ever actually been subscribed, hence no notifications ever
// arriving while the tab is unfocused or closed.
const API_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
    
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushNotificationSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Check notification permission status
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}

// Request notification permission
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported');
    return 'denied';
  }
  
  try {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
}

// Register service worker
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    });
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

// Get VAPID public key from server
export async function getVapidPublicKey(): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/api/push/vapid-public-key`);
    const data = await response.json();
    return data.publicKey;
  } catch (error) {
    console.error('Error fetching VAPID public key:', error);
    return null;
  }
}

// Subscribe to push notifications
export async function subscribeToPushNotifications(
  userId: string,
  userName: string,
  userEmail: string
): Promise<{ success: boolean; subscription?: PushSubscription; error?: string }> {
  try {
    // Check support
    if (!isPushNotificationSupported()) {
      return { success: false, error: 'Push notifications not supported' };
    }
    
    // Request permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'Notification permission denied' };
    }
    
    // Register service worker
    const registration = await registerServiceWorker();
    if (!registration) {
      return { success: false, error: 'Service worker registration failed' };
    }
    
    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;
    
    // Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey();
    if (!vapidPublicKey) {
      return { success: false, error: 'Failed to get VAPID public key' };
    }
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    
    console.log('Push subscription:', subscription);
    
    // Send subscription to server
    const response = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        userName,
        userEmail,
        subscription: subscription.toJSON(),
        userAgent: navigator.userAgent
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to save subscription on server');
    }
    
    await response.json();
    console.log('Push subscription saved');
    
    return { success: true, subscription };
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPushNotifications(): Promise<{ success: boolean; error?: string }> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      return { success: true }; // Already unsubscribed
    }
    
    // Unsubscribe from push manager
    await subscription.unsubscribe();
    
    // Remove from server
    const response = await fetch(`${API_URL}/api/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint
      })
    });
    
    if (!response.ok) {
      console.warn('Failed to remove subscription from server');
    }
    
    console.log('Unsubscribed from push notifications');
    return { success: true };
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

// Check if user is subscribed
export async function isSubscribedToPush(): Promise<boolean> {
  try {
    if (!('serviceWorker' in navigator)) {
      return false;
    }
    
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    return subscription !== null;
  } catch (error) {
    console.error('Error checking push subscription:', error);
    return false;
  }
}

// Get current subscription
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  try {
    if (!('serviceWorker' in navigator)) {
      return null;
    }
    
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('Error getting push subscription:', error);
    return null;
  }
}

// Send test notification
export async function sendTestNotification(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${API_URL}/api/push/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        title: 'Test Notification',
        body: 'This is a test push notification from the DevOps Task Management System'
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to send test notification');
    }
    
    await response.json();
    return { success: true };
  } catch (error) {
    console.error('Error sending test notification:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
